import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

function sessionId() {
  let id = localStorage.getItem("jd_ai_session");
  if (!id) { id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("jd_ai_session", id); }
  return id;
}

export default function AIAssistant() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [cookieOpen, setCookieOpen] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/ai/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);
  useEffect(() => {
    const check = () => setCookieOpen(!localStorage.getItem("jd_cookie"));
    check();
    window.addEventListener("jd-cookie-change", check);
    window.addEventListener("storage", check);
    return () => { window.removeEventListener("jd-cookie-change", check); window.removeEventListener("storage", check); };
  }, []);
  useEffect(() => { if (open && msgs.length === 0 && cfg) setMsgs([{ role: "assistant", content: de ? cfg.greeting_de : cfg.greeting_en }]); }, [open, cfg]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  if (!cfg || !cfg.enabled) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId(), message: text, lang });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply || (de ? "Entschuldigung, keine Antwort." : "Sorry, no reply.") }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", content: apiErr(err), error: true }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ai-open-button"
          className={`fixed right-5 z-[60] flex items-center gap-2 rounded-full bg-primary text-primary-foreground pl-4 pr-5 py-3 shadow-lg shadow-primary/30 hover:scale-105 active:scale-95 transition-all duration-300 ${cookieOpen ? "bottom-40 sm:bottom-36" : "bottom-5"}`}>
          <Sparkles size={18} /> <span className="font-semibold text-sm hidden sm:inline">{cfg.name || (de ? "KI-Assistent" : "AI Assistant")}</span>
        </button>
      )}

      {open && (
        <div data-testid="ai-panel"
          className={`fixed z-[60] bg-background border border-border shadow-2xl flex flex-col overflow-hidden transition-all duration-300
                     inset-0 sm:inset-auto sm:right-5 sm:w-[380px] sm:h-[560px] sm:max-h-[80vh] sm:rounded-2xl ${cookieOpen ? "sm:bottom-36" : "sm:bottom-5"}`}>
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/20"><Sparkles size={16} /></span>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm truncate">{cfg.name || (de ? "KI-Assistent" : "AI Assistant")}</div>
                <div className="text-[11px] opacity-90">{de ? "Fragen zu unseren Leistungen" : "Ask about our services"}</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-close-button" className="p-1.5 rounded-full hover:bg-black/20 transition-colors"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/30" data-testid="ai-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line leading-relaxed
                  ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm"
                    : m.error ? "bg-destructive/15 text-destructive rounded-bl-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-card border border-border rounded-2xl rounded-bl-sm px-3.5 py-2.5"><Loader2 size={16} className="animate-spin text-primary" /></div></div>}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="p-3 border-t border-border flex items-center gap-2 shrink-0 bg-background">
            <input value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
              placeholder={de ? "Nachricht schreiben…" : "Type a message…"} data-testid="ai-input"
              className="flex-1 rounded-full border border-border bg-card px-4 py-2.5 text-sm outline-none focus:border-primary" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="ai-send-button"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40 hover:scale-105 transition-transform shrink-0">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
